import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useGameStore } from './store/gameStore';
import { decisions } from './data/decisions';
import { achievements } from './data/achievements';
import { lifeStages } from './data/lifeStages';
import { useGameLogic } from './hooks/useGameLogic';
import { useTimeManagement } from './hooks/useTimeManagement';
import Header from './components/Header';
import Dashboard from './components/NewDashboard';
import DecisionModal from './components/DecisionModal';
import IntroScreen from './components/IntroScreen';
import GameComplete from './components/GameComplete';
import Timeline from './components/Timeline';
import Achievements from './components/Achievements';
import Settings from './components/Settings';
import GameModeSelector from './components/GameModeSelector';
import EducationalLesson from './components/EducationalLesson';
import ChallengeProgress from './components/ChallengeProgress';
import { challengeScenarios, educationalModules } from './data/gameModes';

function App() {
  const { 
    gamePhase, 
    lifeStages: storeLifeStages, 
    decisions: storeDecisions, 
    allAchievements: storeAchievements,
    gameMode,
    challengeScenario,
    educationalModule,
    modeObjectives,
    modeProgress,
    startGame,
    resetGame
    // setGameMode,
    // setChallengeScenario,
    // setEducationalModule,
    // setModeObjectives,
    // updateModeProgress
  } = useGameStore();

  const [showGameModeSelector, setShowGameModeSelector] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<any>(null);

  // Game mode handlers - TypeScript fix
  const handleGameModeSelected = (mode: string, scenario?: string, module?: string) => {
    useGameStore.getState().setGameMode(mode as any);
    if (scenario) {
      useGameStore.getState().setChallengeScenario(scenario);
      const selectedScenario = challengeScenarios.find(s => s.id === scenario);
      if (selectedScenario) {
        useGameStore.getState().setModeObjectives(selectedScenario.objectives);
      }
    }
    if (module) {
      useGameStore.getState().setEducationalModule(module);
      const selectedModule = educationalModules.find(m => m.id === module);
      if (selectedModule) {
        useGameStore.getState().setModeObjectives(selectedModule.lessons.map(l => l.title));
      }
    }
    setShowGameModeSelector(false);
    startGame();
  };

  const handleStartNewGame = () => {
    setShowGameModeSelector(true);
  };

  const handleLessonComplete = (score: number) => {
    setCurrentLesson(null);
    // Update progress based on score
    if (score >= 80) {
      useGameStore.getState().updateModeProgress({ 
        completedObjectives: [...modeProgress.completedObjectives, currentLesson?.title] 
      });
    }
  };

  const handleSkipLesson = () => {
    setCurrentLesson(null);
  };

  // Initialize game logic
  useGameLogic();
  
  // Initialize time management
  useTimeManagement();

  // Initialize data
  useEffect(() => {
    console.log('App: Loading data', { 
      lifeStages: lifeStages.length, 
      decisions: decisions.length, 
      achievements: achievements.length,
      habits: 0
    });
    useGameStore.setState({
      lifeStages,
      decisions,
      allAchievements: achievements,
    });
  }, []);

  // Ensure decisions are available when game starts
  useEffect(() => {
    if (gamePhase === 'playing') {
      const { availableDecisions, decisions, currentStage } = useGameStore.getState();
      if (availableDecisions.length === 0 && decisions.length > 0) {
        console.log('App: No decisions available, adding stage decisions for:', currentStage);
        const { currentAge } = useGameStore.getState();
        const stageDecisions = decisions
          .filter(d => {
            const stageMatch = d.stage === currentStage;
            const ageMatch = (!d.minAge || currentAge >= d.minAge) && (!d.maxAge || currentAge <= d.maxAge);
            return stageMatch && ageMatch;
          })
          .map(d => d.id);
        if (stageDecisions.length > 0) {
          useGameStore.setState({
            availableDecisions: stageDecisions
          });
        }
      }
    }
  }, [gamePhase]);

  if (gamePhase === 'intro') {
    if (showGameModeSelector) {
      return <GameModeSelector onModeSelected={handleGameModeSelected} onBack={() => setShowGameModeSelector(false)} />;
    }
    return <IntroScreen onStart={handleStartNewGame} />;
  }

  if (gamePhase === 'completed') {
    return <GameComplete />;
  }

  if (gamePhase === 'dead') {
    const { currentAge, causeOfDeath } = useGameStore.getState();
    
    const getDeathMessage = () => {
      switch (causeOfDeath) {
        case 'suicide':
          return {
            emoji: '💸',
            title: 'Game Over - Suicide',
            message: `Your debt became overwhelming at age ${currentAge}. The financial pressure was too much to bear.`,
            bgColor: 'from-red-50 to-red-100'
          };
        case 'health':
          return {
            emoji: '💀',
            title: 'Game Over',
            message: `Your health reached zero at age ${currentAge}`,
            bgColor: 'from-red-50 to-red-100'
          };
        case 'old_age':
          return {
            emoji: '👴',
            title: 'Game Over - Natural Death',
            message: `You passed away peacefully at age ${currentAge}`,
            bgColor: 'from-gray-50 to-gray-100'
          };
        default:
          return {
            emoji: '💀',
            title: 'Game Over',
            message: `Your life ended at age ${currentAge}`,
            bgColor: 'from-red-50 to-red-100'
          };
      }
    };

    const deathInfo = getDeathMessage();
    
    return (
      <div className={`h-screen flex items-center justify-center bg-gradient-to-br ${deathInfo.bgColor}`}>
        <div className="text-center">
          <div className="text-8xl mb-6">{deathInfo.emoji}</div>
          <h1 className="text-4xl font-bold text-red-800 mb-4">{deathInfo.title}</h1>
          <p className="text-xl text-red-600 mb-8">{deathInfo.message}</p>
          <button
            onClick={() => resetGame()}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Header />
        
        {/* Educational Lesson Modal */}
        {currentLesson && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <EducationalLesson 
              lesson={currentLesson} 
              onComplete={handleLessonComplete}
              onSkip={handleSkipLesson}
            />
          </div>
        )}
        
        <main className="flex-1">
          <Routes>
            <Route path="/" element={
              <div>
                {/* Challenge Progress */}
                {gameMode === 'challenge' && challengeScenario && (
                  <div className="p-6">
                    <ChallengeProgress 
                      scenario={challengeScenarios.find(s => s.id === challengeScenario)!}
                      currentAge={useGameStore.getState().currentAge}
                      stats={useGameStore.getState().stats}
                      money={useGameStore.getState().money}
                      debt={useGameStore.getState().stats.debt}
                      completedObjectives={modeProgress.completedObjectives}
                    />
                  </div>
                )}
                <Dashboard />
              </div>
            } />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="bg-slate-800/30 backdrop-blur-sm border-t border-slate-700/50 py-6">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">LR</span>
                </div>
                <span className="text-slate-300 text-sm">© 2025 SppefRun Life</span>
              </div>
              <div className="flex items-center space-x-6 text-slate-400 text-sm">
                <span>Experience the journey of a lifetime</span>
                <span>•</span>
                <span>Made for HackYeah2025 by WMS_DEV</span>
              </div>
            </div>
          </div>
        </footer>

        <DecisionModal />
      </div>
    </Router>
  );
}

export default App;
